import React, { useEffect } from 'react';
import { Link, usePage } from '@inertiajs/react';
import BrandMenu from './BrandMenu';
import useWikiNav from '../../hooks/useWikiNav';
import { freshness, STALE_DAYS } from '../../utils/freshness';
import { useWikiShell } from './shell';
import type { WikiPageSummary } from '../../types/wiki';

// wiki 的左边栏。品牌块 + 三个高频入口 + wiki 自己的导航（最近编辑、最久没动、
// 新建、回收站）。
//
// 标签**不在这里**（2026-08-28 拿掉，理由写在下面那块注释里）。这一栏收的是
// 「跳过去」的东西；「筛一筛」在 ⌘K 面板里。
//
// **全站导航在这里，因为 wiki 下没有 `Header` 了**（2026-08-26 第二轮：用户
// 「按那个UI来」）。稿子画的是 100vh 两栏、没有顶部横条，于是 `Header` 那六个
// 顶级项和整个账户下拉必须有落点 —— 高频的三个（待办 / wiki / 日志）摊在下面
// 那几行，其余全部收进品牌块那个 `⌄`（见 BrandMenu.tsx）。**一项都不能少**：
// `Header.tsx` 的注释写着那些页面「删掉就等于让页面失联」，全前端只有它一个入口。
//
// 窄屏是抽屉 + 遮罩，形状照 `Todo/Sidebar.tsx`（`open` / `onClose`、Esc 关、
// `sm:` 之后变静态列）。**只挪形状不挪代码**：那个组件的内容是 /todo 专有的
// （智能列表、项目、今日事实），挪过来就得先把它一半的东西删掉。
//
// 设计稿里每一行页面前面都有一个 emoji（🚢🔐🧪）。这一期没有 icon 列，所以
// 那一格**不渲染**，而不是渲染一个占位的 📄——给每一页戴同一顶帽子比不戴更吵，
// 而且会让人以为那是可以改的。
type SidebarProps = {
  open: boolean;
  onClose: () => void;
};

// 边栏里的一行。设计稿：27px 高、6px 圆角、hover 只换底色、行与行之间没有
// 分割线——分割线会把「一列可点的东西」变成「一张表」。
const ROW =
  'flex h-[27px] w-full items-center gap-[7px] rounded-md px-2 text-left text-[13.5px] ' +
  'text-todo-ink2 hover:bg-wiki-sideHover';
const ROW_ON = 'bg-wiki-sideActive font-medium text-todo-ink hover:bg-wiki-sideActive';

const GROUP =
  'mx-2 mb-0.5 mt-3.5 flex items-center gap-1.5 text-[11.5px] font-semibold ' +
  'tracking-wide text-todo-muted';

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  // 边栏数据走共享 props（Web::WikisController 的 inertia_share），所以四个
  // wiki 页面都拿得到，页面组件自己不用管这件事。
  //
  // 可能是 undefined：局部重载（router.reload({ only: […] })）会按 dot 路径把
  // 没被点名的共享 props 整块滤掉。那时这个组件仍在挂载状态但手里没数据，所以
  // 下面每一块都自己判空 —— 这只保证不白屏，不保证边栏是对的。正面防线是
  // test/controllers/web/wikis_controller_test.rb 里那条「四个 action 都出
  // wikiNav」的契约测试。
  const nav = useWikiNav();
  const { toggleCollapsed, focusSearch, startNewPage } = useWikiShell();
  // usePage().url 是「路径 + 查询串」，不是 window.location —— SSR 与首屏水合
  // 时 window 可能还不在。
  const path = usePage().url;

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // 「/wiki」精确匹配才算在列表页：/wiki/xxxx 也以 /wiki 开头。
  const onIndex = path === '/wiki' || path.startsWith('/wiki?');
  const onTrash = path.startsWith('/wiki/trash');

  const pageRow = (page: WikiPageSummary, tail?: string) => {
    const current = path.startsWith(`/wiki/${page.url_key}`);
    return (
      <Link
        key={page.url_key}
        href={`/wiki/${page.url_key}`}
        onClick={onClose}
        className={`${ROW} ${current ? ROW_ON : ''}`}
        title={page.title}
      >
        <span className="truncate">{page.title}</span>
        {tail && <span className="ml-auto shrink-0 text-[11.5px] text-todo-muted">{tail}</span>}
      </Link>
    );
  };

  return (
    <>
      {/* 遮罩只在窄屏且抽屉打开时存在。sm: 之后侧栏是静态列，不需要遮罩。 */}
      {open && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-20 bg-black/30 sm:hidden"
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'z-30 flex w-[260px] flex-col overflow-hidden border-r border-todo-line bg-todo-side',
          // 窄屏：定位在视口上的抽屉，从左侧滑入。
          'fixed inset-y-0 left-0 transition-transform sm:static sm:w-auto sm:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="px-2 pb-1.5 pt-2.5">
          <div className="flex items-center gap-0.5">
            <div className="min-w-0 flex-1">
              <BrandMenu />
            </div>
            {/* 收起按钮在品牌行（设计稿的位置）。它随边栏一起消失是对的 ——
                展开回来的那个 `»` 在顶条上，只在收起时出现，所以任何时刻
                「切换边栏」都恰好只有一个可见的控件。 */}
            <button
              type="button"
              onClick={toggleCollapsed}
              title="收起边栏"
              className="hidden h-[26px] w-[26px] shrink-0 place-items-center rounded-md
                         text-[14px] text-todo-muted hover:bg-wiki-sideHover
                         hover:text-todo-ink2 sm:grid"
            >
              «
            </button>
          </div>

          {/* 「搜索」的动作是「（必要时先回列表页，然后）把光标送进那个搜索框」
              —— 搜索条内嵌在列表页里，不是独立页面、也不是浮层。
              已经在列表页时不导航，否则会把当前的筛选条件冲掉。
              ⌘K 那个键帽把快捷键一并教出来，省掉一份「有哪些快捷键」的文档。 */}
          <button
            type="button"
            onClick={() => {
              onClose();
              focusSearch();
            }}
            className={ROW}
          >
            <span className="w-[17px] shrink-0 text-center text-[13px]">🔍</span>
            <span>搜索</span>
            <span
              className="ml-auto shrink-0 rounded border border-todo-line2 px-1 py-px
                         font-mono text-[10.5px] text-todo-muted"
            >
              ⌘K
            </span>
          </button>

          {/* 设计稿那三行。它们是**全站**的高频入口，不是 wiki 内部的导航 ——
              所以放在这一格（与下面那些分组之间有留白隔开），而不是混进
              「最近编辑」上面。
              match 用前缀判：/todo 有五个视图（/todo/today、/todo/backlog…），
              按 href 精确判的话访问 /todo/backlog 时这一行不会亮。 */}
          <Link href="/todo/today" onClick={onClose} className={ROW}>
            <span className="w-[17px] shrink-0 text-center text-[13px]">🏠</span>
            <span>待办</span>
          </Link>

          <Link href="/wiki" onClick={onClose} className={`${ROW} ${onIndex ? ROW_ON : ''}`}>
            <span className="w-[17px] shrink-0 text-center text-[13px]">📚</span>
            <span>wiki</span>
            {nav && (
              <span className="ml-auto shrink-0 text-[11.5px] text-todo-muted">{nav.total}</span>
            )}
          </Link>

          <Link href="/worklog" onClick={onClose} className={ROW}>
            <span className="w-[17px] shrink-0 text-center text-[13px]">📝</span>
            <span>日志</span>
          </Link>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {nav && nav.recent.length > 0 && (
            <>
              <div className={GROUP}>最近编辑</div>
              {nav.recent.map((page) => pageRow(page))}
            </>
          )}

          {/* 这里曾经是标签云。2026-08-28 整块拿掉，搬进 ⌘K 面板，三条理由：
                1. 它与列表页那条筛选条显示同一份东西 —— 同一件事一屏两遍；
                2. 点它会把人从正在读的文章弹回列表页 —— 那是**导航跳转**，
                   而 chip 的样子承诺的是「就地筛」，两者对不上；
                3. 截到 49 个（PR #36）仍是这一栏里最重的一块，260px 宽约 12 行，
                   把下面的「最久没动」压出首屏 —— todo 356 抱怨的就是这个。
              面板是浮层，「全部 202 个」展得起；边栏是常驻结构，永远展不起。 */}

          {/* 「最久没动」在边栏（设计稿的判断）：它是 §7.7 那个「你在忘什么」的
              信号，而放在列表页的下半屏意味着只有翻到底的人才看得见。
              **只在这里有一份**：列表页改成整库按「多久没动」分段之后，最久的
              那几页天然就是最后一段的尾巴，再单列一次就是同一批东西列两遍。 */}
          {nav && nav.stalest.length > 0 && (
            <>
              <div className={GROUP}>最久没动</div>
              {nav.stalest.map((page) =>
                pageRow(
                  page,
                  page.days_since_update > STALE_DAYS
                    ? `${page.days_since_update} 天`
                    : freshness(page.days_since_update, page.updated_at),
                ),
              )}
            </>
          )}
        </div>

        <div className="border-t border-todo-line px-2 pb-2 pt-1.5">
          {/* 新建：建一页「无标题」的，然后跳进那一页的编辑态、光标落在标题上。
              动作本身在 `WikiLayout`（shell 的 `startNewPage`），这里只是调用方
              之一 —— 与列表页顶条那个 ＋ 是同一个动作。

              曾经这里自己写了一遍「必要时导航 + focusById 列表页那个新建框」，
              而那个框是**条件渲染**的、开关只有列表页碰得到，于是这一行是个死
              按钮：在文章页点它只是跳回列表页（框不出现），已经在列表页点它
              完全没反应（任务 #487）。现在建页不再需要列表页在场，这一行从任何
              一个 wiki 页面上点都成立。 */}
          <button
            type="button"
            onClick={() => {
              onClose();
              startNewPage();
            }}
            className={ROW}
          >
            <span className="w-[17px] shrink-0 text-center text-[13px]">＋</span>
            <span>新建页面</span>
          </button>

          <Link
            href="/wiki/trash"
            onClick={onClose}
            className={`${ROW} ${onTrash ? ROW_ON : ''}`}
          >
            <span className="w-[17px] shrink-0 text-center text-[13px]">🗑</span>
            <span>回收站</span>
            {/* 0 条时不显示数字：一个常驻的「0」是纯噪音（照 Todo/Sidebar 里
                「近期」与「回收站」不显示计数的同一条判断）。 */}
            {nav && nav.trashCount > 0 && (
              <span className="ml-auto shrink-0 text-[11.5px] text-todo-muted">
                {nav.trashCount}
              </span>
            )}
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
